import {
  EventClass,
  EventCourse,
  EventRequest
} from "../../../entities/courseEvent/courseEvent";
import { interfaceEventRepository } from "../../../../infrastructure/repository/impl/event/event_repository";
import { ContentComment, Instructor, User } from "../../../entities/entities";
import { EventClassRules } from "./eventClassRules";

export interface EventUseCases {
  /** Returns the event listing.
   * @see EventCourse */
  list(): Promise<EventCourse[]>;

  /** Return event information by id. */
  getEventCourse(eventID: number): Promise<EventRequest>;

  /** Request to enroll the user in the class. */
  enrollUser(user: User, eventClass: EventClass): Promise<void>;

  /** Request to remove the user's enrollment in the class. */
  unEnrollUser(user: User, eventClass: EventClass): Promise<void>;

  /** Get all comments of an event. */
  getComments(eventId: number): Promise<ContentComment[]>;

  confirmCheckinQrCode(token: string, date: string): Promise<void>;

  getInstructors(eventID: number, classID: number): Promise<Instructor[]>;

  generateCertificate(eventID: number, classID: number): Promise<string>;
}

class _EventUseCases implements EventUseCases {
  async list(): Promise<EventCourse[]> {
    return await interfaceEventRepository.list();
  }

  async getEventCourse(eventID: number): Promise<EventRequest> {
    return await interfaceEventRepository.getEventCourse(eventID);
  }

  async enrollUser(user: User, eventClass: EventClass): Promise<void> {
    const alreadyEnrolledError = EventClassRules.alreadyEnrolledError(
      user,
      eventClass
    );
    if (alreadyEnrolledError) {
      throw alreadyEnrolledError;
    }

    const maxCapacityReachedError = EventClassRules.maxCapacityReachedError(
      user,
      eventClass
    );
    if (maxCapacityReachedError) {
      throw maxCapacityReachedError;
    }

    const eventClassIsFinishedError = EventClassRules.eventClassIsFinishedError(
      eventClass
    );
    if (eventClassIsFinishedError) {
      throw eventClassIsFinishedError;
    }

    return await interfaceEventRepository.enrollUser(eventClass);
  }

  async unEnrollUser(user: User, eventClass: EventClass): Promise<void> {
    const eventClassIsFinishedError = EventClassRules.eventClassIsFinishedError(
      eventClass
    );
    if (eventClassIsFinishedError) {
      throw eventClassIsFinishedError;
    }

    return await interfaceEventRepository.unEnrollUser(eventClass);
  }

  async getComments(eventId: number): Promise<ContentComment[]> {
    return await interfaceEventRepository.getComments(eventId);
  }

  async confirmCheckinQrCode(token: string, date: string): Promise<void> {
    return await interfaceEventRepository.confirmCheckinQrCode(token, date);
  }

  async getInstructors(
    eventID: number,
    classID: number
  ): Promise<Instructor[]> {
    return await interfaceEventRepository.getInstructors(eventID, classID);
  }

  async generateCertificate(eventID: number, classID: number): Promise<string> {
    return await interfaceEventRepository.generateCertificate(eventID, classID);
  }
}

export const InterfaceEventUseCases: EventUseCases = new _EventUseCases();
